import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Bookmark, BookmarkOutline } from '@lyearn/icons';
import { Box } from '@lyearn/ui';
import useContentBookmark from '@/features/bookmark/hooks/useContentBookmark';
import { useLongPressed } from '@/modules/content/context/LongPress';
import { ResourceTypeEnum } from '@/types/schema';

import * as BaseCard from '../../../.';
import { useBaseCardContext } from '../../../BaseCardContext';
import { useBaseCard } from '../../../Provider';
import useOptions from '../../useOptions';

const OptionsWithBookmark: React.FC<
  React.PropsWithChildren<{
    contentType: ResourceTypeEnum;
    contentId: string;
  }>
> = ({ children, contentType, contentId }) => {
  const { isLongPress, off } = useLongPressed();

  const { bookmark, name, onBookmarkUpdate, publicAccessConfig } = useBaseCard();
  const { createOnAction, addItemsAtIndex } = useOptions({
    contentId,
    contentType,
    contentName: name ?? undefined,
    onClose: off,
    publicAccessConfig,
  });
  const { t } = useTranslation('Common');

  const {
    bookmarks: { allowUndo },
  } = useBaseCardContext();

  const { onToggleBookmark, updatedBookmarkId } = useContentBookmark({
    contentId,
    contentType,
    bookmarkId: bookmark?.id,
    allowUndo,
    onBookmarkUpdate,
  });

  const items = React.useMemo(() => {
    return addItemsAtIndex(
      [
        {
          id: 'Bookmark',
          label: updatedBookmarkId ? t('ContentCards.RemoveBookmark') : t('ContentCards.Bookmark'),
          startIcon: (
            <Box className="flex h-24 w-24 items-center justify-center rounded-full me-20">
              {updatedBookmarkId ? (
                <Bookmark className="h-24 w-24" fillColorToken="icon-primary" />
              ) : (
                <BookmarkOutline className="h-24 w-24" fillColorToken="icon-primary" />
              )}
            </Box>
          ),
          appearance: 'primary',
          overrides: {
            // @ts-ignore
            // ask harshit to see if we can make overrides partial @iamsaumya
            Label: {
              variant: 'body-short-01',
            },
          },
        },
      ],
      1,
    );
  }, [addItemsAtIndex, updatedBookmarkId, t]);

  const onAction = React.useMemo(() => {
    return createOnAction((actionType, item) => {
      switch (actionType) {
        case 'Bookmark': {
          onToggleBookmark();
          off();
          break;
        }
        default:
          throw new Error(`Unknown action type: ${actionType}`);
      }
    });
  }, [createOnAction, off, onToggleBookmark]);

  return (
    <>
      {children}
      <BaseCard.Options items={items} showOptions={isLongPress} onAction={onAction} onClose={off} />
    </>
  );
};

export default OptionsWithBookmark;
